"use client";
import { api } from "@smart-polling/backend/convex/_generated/api";
import { usePaginatedQuery, useQuery } from "convex/react";
import { Copy, Share2 } from "lucide-react";
import Link from "next/link";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export default function MyPollsPage() {
  const currentUser = useQuery(api.users.current);
  const polls = usePaginatedQuery(api.polls.getMyPolls, {}, { initialNumItems: 10 });

  const handleShare = async (url: string, title?: string) => {
    try {
      const nav: Navigator | undefined = typeof window !== "undefined" ? window.navigator : undefined;
      type NavigatorWithShare = Navigator & { share: (data: ShareData) => Promise<void> };
      const canWebShare = (n: Navigator | undefined): n is NavigatorWithShare => {
        return Boolean(n && typeof (n as Navigator & Partial<{ share: unknown }>).share === "function");
      };
      type NavigatorWithClipboard = Navigator & { clipboard: { writeText: (text: string) => Promise<void> } };
      const hasClipboard = (n: Navigator | undefined): n is NavigatorWithClipboard => {
        const candidate = n as (Navigator & { clipboard?: { writeText?: unknown } }) | undefined;
        return Boolean(candidate && typeof candidate.clipboard?.writeText === "function");
      };

      const shareData = { title: title ?? "Poll", text: "Participate in this poll", url };

      if (canWebShare(nav)) {
        await nav.share(shareData);
        toast.success("Share sheet opened");
        return;
      }

      if (hasClipboard(nav)) {
        const n = nav as NavigatorWithClipboard;
        await n.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
        return;
      }

      const el = document.createElement("input");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Unable to share the link");
    }
  };

  const handleCopy = async (url: string) => {
    try {
      const nav: Navigator | undefined = typeof window !== "undefined" ? window.navigator : undefined;
      type NavigatorWithClipboard = Navigator & { clipboard: { writeText: (text: string) => Promise<void> } };
      const hasClipboard = (n: Navigator | undefined): n is NavigatorWithClipboard => {
        const candidate = n as (Navigator & { clipboard?: { writeText?: unknown } }) | undefined;
        return Boolean(candidate && typeof candidate.clipboard?.writeText === "function");
      };
      if (hasClipboard(nav)) {
        await nav.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
        return;
      }
      const el = document.createElement("input");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Unable to copy the link");
    }
  };

  if (currentUser === undefined) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-semibold text-2xl">My Polls</h1>
        <Link href="/polls/create">
          <Button>Create</Button>
        </Link>
      </div>

      <div className="grid gap-4">
        {polls.results.map((p) => (
          <Card key={p._id}>
            <CardHeader>
              <CardTitle>{p.title}</CardTitle>
              {p.description && <CardDescription>{p.description}</CardDescription>}
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Link href={`/polls/${p._id}`}>
                <Button>Open</Button>
              </Link>
              <Link href={`/polls/${p._id}/results`}>
                <Button variant="outline">Results</Button>
              </Link>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  handleShare(`${typeof window !== "undefined" ? window.location.origin : ""}/polls/${p._id}`, p.title)
                }
              >
                <Share2 />
                Share
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  handleCopy(`${typeof window !== "undefined" ? window.location.origin : ""}/polls/${p._id}`)
                }
              >
                <Copy />
                Copy URL
              </Button>
            </CardContent>
          </Card>
        ))}

        {polls.status === "CanLoadMore" && <Button onClick={() => polls.loadMore(10)}>Load more</Button>}

        {!polls.results.length && <div className="text-muted-foreground">You haven't created any polls yet.</div>}
      </div>
    </div>
  );
}
